import type { RestApiClients } from '@post-office/shared-contracts/rest-api-clients';
import { getTapTraitsForSite } from '@post-office/util-tap-traits';

const ROVO_ENABLED_SITES_WITHOUT_FAST_SHIFT_TRAIT = 'rovo_enabled_sites_without_fast_shift';

function isSiteRovoEnabledWithoutFastShift(traits: Record<string, unknown>): boolean {
	return traits[ROVO_ENABLED_SITES_WITHOUT_FAST_SHIFT_TRAIT] === true;
}

/** Minimal request shape accepted by assertRovoOnboardingEligibility (restApiClients can be RestApiClients or request context from in-app/v2). */
export type RovoOnboardingEligibilityRequest = {
	logger: { info: (ctx: unknown, msg: string) => void; error: (ctx: unknown, msg: string) => void };
	orchestrationContext?: { tenantId?: string; orgId?: string };
	restApiClients?:
		| RestApiClients
		| {
				tcsClient?: {
					settingsService: {
						getRovoOnboardingStateOrgLevel: (
							orgId: string,
						) => Promise<{ data?: { currentStep?: string } }>;
					};
				};
		  };
	tapProvider?: unknown;
};

export type RovoOnboardingEligibilityMessage = {
	messageInstanceId: string;
	messageTemplateId?: string;
	triggerId?: string;
	context: unknown;
};

/**
 * Asserts that the site/org is eligible for the Rovo onboarding banner (TAP trait
 * rovo_enabled_sites_without_fast_shift + org-level onboarding state NOT_STARTED).
 * Throws with a descriptive error if not eligible; use placementLabel in log messages.
 */
export async function assertRovoOnboardingEligibility(
	request: RovoOnboardingEligibilityRequest,
	message: RovoOnboardingEligibilityMessage,
	placementLabel: string,
): Promise<void> {
	const { logger, orchestrationContext, restApiClients, tapProvider } = request;
	const {
		messageInstanceId,
		messageTemplateId = 'confluence-rovo-onboarding-banner',
		triggerId = 'unknown',
		context,
	} = message;
	const tenantId = (context as { cloudId?: string }).cloudId ?? orchestrationContext?.tenantId;

	if (!tenantId || typeof tenantId !== 'string') {
		logger.error(
			{ messageInstanceId, messageTemplateId, triggerId },
			`${placementLabel} - tenant id not available, dropping message`,
		);
		throw new Error('Tenant ID not available');
	}

	const orgId = orchestrationContext?.orgId;
	if (!orgId || typeof orgId !== 'string') {
		logger.error(
			{ messageInstanceId, messageTemplateId, triggerId },
			`${placementLabel} - org id not available, dropping message`,
		);
		throw new Error('Org ID not available');
	}

	if (!tapProvider) {
		logger.error(
			{ messageInstanceId, messageTemplateId, triggerId },
			`${placementLabel} - tap provider not available, dropping message`,
		);
		throw new Error('Tap provider not available');
	}

	let siteTapTraits: Record<string, string | boolean | number> | undefined;
	try {
		siteTapTraits = await getTapTraitsForSite({
			siteId: tenantId,
			tapProvider: tapProvider as Parameters<typeof getTapTraitsForSite>[0]['tapProvider'],
			logger: logger as Parameters<typeof getTapTraitsForSite>[0]['logger'],
		});
	} catch (error) {
		logger.error(
			{ messageInstanceId, messageTemplateId, triggerId, error },
			`${placementLabel} - failed to fetch TAP site traits, dropping message`,
		);
		throw new Error('Failed to fetch TAP site traits');
	}

	if (!isSiteRovoEnabledWithoutFastShift(siteTapTraits as Record<string, unknown>)) {
		logger.info(
			{ messageInstanceId, messageTemplateId, triggerId, tenantId },
			`${placementLabel} - site not in rovo_enabled_sites_without_fast_shift, dropping message`,
		);
		throw new Error('Site not in rovo_enabled_sites_without_fast_shift');
	}

	if (!restApiClients?.tcsClient) {
		logger.error(
			{ messageInstanceId, messageTemplateId, triggerId },
			`${placementLabel} - TCS client not available, dropping message`,
		);
		throw new Error('TCS client not available');
	}

	let rovoOnboardingState: { currentStep?: string };
	try {
		const response =
			await restApiClients.tcsClient.settingsService.getRovoOnboardingStateOrgLevel(orgId);
		rovoOnboardingState = response.data ?? {};
	} catch (error) {
		logger.error(
			{ messageInstanceId, messageTemplateId, triggerId, orgId, error },
			`${placementLabel} - failed to fetch rovo-onboarding-state-org-level, dropping message`,
		);
		throw new Error('Failed to fetch rovo-onboarding-state-org-level');
	}

	if (rovoOnboardingState.currentStep !== 'NOT_STARTED') {
		logger.info(
			{ messageInstanceId, messageTemplateId, triggerId, orgId },
			`${placementLabel} - org currentStep is not NOT_STARTED, dropping message`,
		);
		throw new Error('Org rovo onboarding state is not NOT_STARTED');
	}
}
